import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatLongDate } from '../../utils/dateFormatter';
import { FileText, Hammer, ClipboardList } from 'lucide-react';
import ComposerSheet from '../ui/ComposerSheet';

const VendorInquiryDialogs = ({
  // Cancellation Dialog
  cancellingInquiry,
  setCancellingInquiry,
  cancellationReason,
  setCancellationReason,
  handleCancelInquiry,
  
  // Decline Dialog
  decliningInquiry,
  setDecliningInquiry,
  declineReason,
  setDeclineReason,
  customDeclineReason,
  setCustomDeclineReason,
  handleDeclineInquiry,
  
  // On Hold Dialog
  onHoldInquiry,
  setOnHoldInquiry,
  onHoldNote,
  setOnHoldNote,
  putOnHold,
  
  // Progress Note Dialog
  progressNoteInquiry,
  setProgressNoteInquiry,
  progressNote,
  setProgressNote,
  addProgressNote,
  
  // Start Work Dialog
  startingWorkInquiry,
  setStartingWorkInquiry,
  startWorkNote,
  setStartWorkNote,
  markInProgress,
  loading,
  
  // Add Progress Note Dialog
  addingNote,
  setAddingNote,
  newProgressNote,
  setNewProgressNote,
  submitProgressNote,
  
  // Mark Complete Dialog
  markingComplete,
  setMarkingComplete,
  handleMarkComplete
}) => {
  return (
    <>
      {/* ═══ MODALS (kept as-is — short confirmations, no heavy typing) ═══ */}

      {/* Decline Inquiry Dialog */}
      {decliningInquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Decline This Request</CardTitle>
              <CardDescription>Let the customer know this request isn't a good fit for your services</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="decline-reason">Why can't you help? *</Label>
                <Select value={declineReason} onValueChange={setDeclineReason}>
                  <SelectTrigger id="decline-reason"><SelectValue placeholder="Select a reason..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not my specialty">Not my specialty (e.g., I only do birthdays, not weddings)</SelectItem>
                    <SelectItem value="Date unavailable">I'm not available on that date</SelectItem>
                    <SelectItem value="Outside service area">Location is outside my service area</SelectItem>
                    <SelectItem value="Budget mismatch">Budget doesn't match my pricing</SelectItem>
                    <SelectItem value="Capacity">Event size is too large/small for what I do</SelectItem>
                    <SelectItem value="Other">Other reason</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {declineReason === "Other" && (
                <div>
                  <Label htmlFor="custom-decline-reason">Please explain *</Label>
                  <Textarea id="custom-decline-reason" value={customDeclineReason} onChange={(e) => setCustomDeclineReason(e.target.value)} placeholder="Explain why you can't help with this request..." rows={3} />
                  <p className="text-xs text-gray-500 mt-1">This will be shared with the customer</p>
                </div>
              )}
              <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                <p className="mb-2"><strong>What happens next:</strong></p>
                <ul className="text-xs space-y-1 ml-4 list-disc">
                  <li>The customer will be notified you can't help with this request</li>
                  <li>This request will be marked as &quot;Declined&quot;</li>
                  <li>The customer can still message you if needed</li>
                  <li>This helps keep your request list organized</li>
                </ul>
              </div>
              <div className="flex space-x-2">
                <Button onClick={handleDeclineInquiry} className="flex-1 bg-red-600 hover:bg-red-700" disabled={!declineReason || (declineReason === "Other" && !customDeclineReason.trim())}>Decline Inquiry</Button>
                <Button variant="outline" onClick={() => { setDecliningInquiry(null); setDeclineReason(''); setCustomDeclineReason(''); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cancellation Reason Dialog */}
      {cancellingInquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Mark as "Did not move forward"</CardTitle>
              <CardDescription>Please select a reason (optional but recommended)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="cancellation-reason">Reason</Label>
                <Select value={cancellationReason} onValueChange={setCancellationReason}>
                  <SelectTrigger id="cancellation-reason"><SelectValue placeholder="Select a reason..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Customer declined">Customer declined</SelectItem>
                    <SelectItem value="Vendor unavailable">Vendor unavailable</SelectItem>
                    <SelectItem value="Pricing mismatch">Pricing mismatch</SelectItem>
                    <SelectItem value="Date conflict">Date conflict</SelectItem>
                    <SelectItem value="Customer unresponsive">Customer unresponsive</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-yellow-50 p-3 rounded text-sm text-yellow-800">
                <p><strong>Note:</strong> This will prevent the customer from leaving a review for this inquiry unless they dispute the status.</p>
              </div>
              <div className="flex space-x-2">
                <Button onClick={handleCancelInquiry} className="flex-1">Confirm</Button>
                <Button variant="outline" onClick={() => { setCancellingInquiry(null); setCancellationReason(''); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Put On Hold Dialog */}
      {onHoldInquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Put Request On Hold</CardTitle>
              <CardDescription>Provide a reason for putting this request on hold</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="on-hold-note">Reason</Label>
                <Textarea id="on-hold-note" value={onHoldNote} onChange={(e) => setOnHoldNote(e.target.value)} placeholder="e.g., Waiting for customer to provide final details..." rows={3} />
              </div>
              <div className="flex space-x-2">
                <Button onClick={putOnHold} className="flex-1">Confirm</Button>
                <Button variant="outline" onClick={() => { setOnHoldInquiry(null); setOnHoldNote(''); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mark Complete Confirmation Dialog */}
      {markingComplete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Mark Job Complete?</h3>
            <p className="text-sm text-gray-700 mb-4">This will notify the customer that the work is complete. They will review and pay the final amount.</p>
            <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4">
              <p className="text-sm text-blue-900"><strong>Agreed Price:</strong> ${markingComplete.agreed_price.toFixed(2)}<br /><strong>You&apos;ll Receive:</strong> ${markingComplete.vendor_payout_amount.toFixed(2)}</p>
            </div>
            <div className="flex space-x-3">
              <Button onClick={() => handleMarkComplete(markingComplete.id)} className="flex-1 bg-green-600 hover:bg-green-700">Confirm - Mark Complete</Button>
              <Button variant="outline" onClick={() => setMarkingComplete(null)} className="flex-1">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ COMPOSER SHEETS (keyboard-aware, typing-heavy) ═══ */}

      {/* Progress Note ComposerSheet */}
      <ComposerSheet
        open={!!progressNoteInquiry}
        onClose={() => { setProgressNoteInquiry(null); setProgressNote(''); }}
        title="Add Progress Note"
        subtitle="Add a note about progress on this event"
        icon={<FileText className="h-5 w-5 text-blue-600" />}
        testId="progress-note-composer"
        maxWidth="sm:max-w-md"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setProgressNoteInquiry(null); setProgressNote(''); }} className="flex-1">Cancel</Button>
            <Button onClick={addProgressNote} disabled={!progressNote.trim()} className="flex-1" data-testid="submit-progress-note-btn">Add Note</Button>
          </div>
        }
      >
        <div>
          <Label htmlFor="progress-note" className="text-sm font-medium text-gray-700">Note</Label>
          <Textarea
            id="progress-note"
            value={progressNote}
            onChange={(e) => setProgressNote(e.target.value)}
            placeholder="e.g., Ordered balloons, preparing backdrop setup..."
            rows={4}
            className="mt-1.5"
            autoFocus
            data-testid="progress-note-input"
          />
        </div>
      </ComposerSheet>

      {/* Start Work ComposerSheet */}
      <ComposerSheet
        open={!!startingWorkInquiry}
        onClose={() => { setStartingWorkInquiry(null); setStartWorkNote(''); }}
        title="Start Work"
        subtitle="Let the customer know you've started on their event"
        icon={<Hammer className="h-5 w-5 text-indigo-600" />}
        testId="start-work-composer"
        maxWidth="sm:max-w-md"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setStartingWorkInquiry(null); setStartWorkNote(''); }} disabled={loading} className="flex-1">Cancel</Button>
            <Button onClick={markInProgress} disabled={loading || !startWorkNote.trim()} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="submit-start-work-btn">
              {loading ? 'Starting...' : 'Start Work'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {startingWorkInquiry && (
            <div className="bg-indigo-50 border border-indigo-200 p-3.5 rounded-xl">
              <p className="text-xs font-medium text-indigo-600 mb-0.5">Event Details</p>
              <p className="text-sm font-semibold text-indigo-900">{startingWorkInquiry.event_type} on {formatLongDate(startingWorkInquiry.event_date)}</p>
              <p className="text-xs text-indigo-700 mt-0.5">Location: {startingWorkInquiry.location}</p>
            </div>
          )}
          <div>
            <Label htmlFor="start-work-note" className="text-sm font-medium text-gray-700">Progress Note *</Label>
            <Textarea
              id="start-work-note"
              value={startWorkNote}
              onChange={(e) => setStartWorkNote(e.target.value)}
              placeholder="e.g., Starting work on this event. Ordered supplies and confirmed delivery timeline..."
              rows={4}
              className="mt-1.5"
              autoFocus
              data-testid="start-work-note-input"
            />
            <p className="text-xs text-gray-400 mt-1">This note will be visible to the customer.</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-800">
            <p className="font-medium mb-1">What happens next?</p>
            <p>The request status will change to &quot;In Progress&quot; and the customer will see your note.</p>
          </div>
        </div>
      </ComposerSheet>

      {/* Add Progress Note ComposerSheet */}
      <ComposerSheet
        open={!!addingNote}
        onClose={() => { setAddingNote(null); setNewProgressNote(''); }}
        title="Add Progress Note"
        subtitle="Keep track of what's happening with this inquiry"
        icon={<ClipboardList className="h-5 w-5 text-emerald-600" />}
        testId="add-note-composer"
        maxWidth="sm:max-w-md"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setAddingNote(null); setNewProgressNote(''); }} className="flex-1">Cancel</Button>
            <Button onClick={() => submitProgressNote(addingNote)} disabled={!newProgressNote.trim()} className="flex-1" data-testid="submit-add-note-btn">Add Note</Button>
          </div>
        }
      >
        <div>
          <Label htmlFor="new-progress-note" className="text-sm font-medium text-gray-700">Note *</Label>
          <Textarea
            id="new-progress-note"
            value={newProgressNote}
            onChange={(e) => setNewProgressNote(e.target.value)}
            placeholder="e.g., Ordered supplies, confirmed delivery time with customer..."
            rows={4}
            className="mt-1.5"
            autoFocus
            data-testid="add-note-input"
          />
        </div>
      </ComposerSheet>
    </>
  );
};

export default VendorInquiryDialogs;
